import axios from "axios";
import {Dispatch} from "redux";
import {actions} from "../reducers/PriceCatalogTableReducer";
import {ApiResponse} from "../../_common/types";
import {
  AddEditItemParams,
  GetListParams,
  PriceCatalogResponseBody,
  PriceCatalogForExcelResponseBody,
  GetItemDetailsResponse,
  DeleteItemParams
} from "../types";

export const GET_PRICE_CATALOG = "priceCatalog/getList";
export const GET_PRICE_CATALOG_FOR_EXCEL = "priceCatalog/getFullList";
export const GET_ITEM_DETAILS = "priceCatalog/getItemDetails";
export const ADD_EDIT_ITEM = "priceCatalog/addEditItem";
export const DELETE_ITEM = "priceCatalog/deleteItem";
export const UPLOAD_FILE = "priceCatalog/uploadFile";

export const getList = (action: (payload: any) => any) => (params: GetListParams = {}) =>
  async (dispatch: Dispatch) => {
    const {
      mainCategory = "",
      secondCategory = "",
      serviceDescription = "",
      requestedPageNumber = 0,
      sortByField = 'id',
      sortByOrder = -1
    } = params;

    const data = {
      mainCategory,
      secondCategory,
      serviceDescription,
      requestedPageNumber: parseInt(String(requestedPageNumber), 10),
      sortByField,
      sortByOrder
    };

    try {
      const response = await axios.post<ApiResponse<PriceCatalogResponseBody>>(GET_PRICE_CATALOG, data);

      const {errorCode, body} = response.data;

      if (errorCode < 0) {
        throw new Error("API error");
      }

      const {totalPageCount, currentPageNumber, list, totalResultCount} = body;

      dispatch(
        action({
          totalPageCount,
          currentPageNumber,
          list,
          totalResultCount,
        })
      );
    } catch (error: any) {
      error.clientMessage = "Can't find data";
      dispatch(actions.catchError(error));
    }
  };

export const getListForExcel = (params: GetListParams = {}) => async (dispatch: Dispatch) => {
  const {
    mainCategory = '',
    secondCategory = '',
    serviceDescription = '',
    sortByField = 'id',
    sortByOrder = -1,
  } = params;

  const data = {
    mainCategory,
    secondCategory,
    serviceDescription,
    sortByField,
    sortByOrder,
  };

  try {
    const response = await axios.post<ApiResponse<PriceCatalogForExcelResponseBody>>(
      GET_PRICE_CATALOG_FOR_EXCEL,
      data
    );

    const {errorCode, body} = response.data;

    if (errorCode < 0) {
      throw new Error('API error');
    }

    return body?.list ?? [];
  } catch (error: unknown) {
    (error as Error & { clientMessage?: string }).clientMessage = "Can't find data";
    dispatch(actions.catchError(error as string));
  }
};

export const getItemDetails = async (id: number) => {
  const data = {
    id: parseInt(String(id), 10),
  };

  try {
    const response = await axios.post<ApiResponse<GetItemDetailsResponse>>(
      GET_ITEM_DETAILS,
      data
    );

    const {errorCode, body} = response.data;

    if (errorCode < 0) {
      throw new Error(`API error: ${errorCode}`);
    }

    return body?.details;
  } catch (error: unknown) {
    (error as Error & { clientMessage?: string }).clientMessage = "Can't get item details";
  }
};

export const addEditItem = ({
     id,
     mainCategory,
     secondCategory,
     serviceDescription,
     servicePrice,
     notes,
   }: AddEditItemParams) => async (dispatch: Dispatch) => {
    const data = {
      id: parseInt(String(id), 10),
      mainCategory,
      secondCategory,
      serviceDescription,
      servicePrice: parseFloat(String(servicePrice)),
      notes,
    };
    try {
      const response = await axios.post(ADD_EDIT_ITEM, data);
      const {errorCode, body} = response.data;

      if (errorCode === 0) {
        if (data.id === -1) {
          dispatch(actions.addItem({...data, id: body?.id}))
        } else {
          dispatch(actions.editItem(data))
        }
      }

      return errorCode
    } catch (error: unknown) {
      (error as Error & { clientMessage?: string }).clientMessage = "Can't edit item";
      return -1;
    }
  };

export const deleteItem = ({id}: DeleteItemParams) => async (dispatch: Dispatch) => {
  const data = {id: parseInt(String(id), 10)};
  try {
    const response = await axios.post(DELETE_ITEM, data);
    const {errorCode} = response.data;
    if (errorCode === 0) {
      dispatch(actions.deleteItem(data.id))
    }

    return errorCode
  } catch (error: unknown) {
    (error as Error & { clientMessage?: string }).clientMessage = "Can't delete item";
    return -1;
  }
};

export const uploadFile = async (file: File, fileName: string) => {
  const formData = new FormData();

  formData.append('fileToSave', file);
  formData.append('fileName', fileName);

  return await axios.post(UPLOAD_FILE, formData);
};

export const getPriceCatalogItems = getList(actions.priceCatalogFetched);
export const getNextPriceCatalogItems = getList(actions.priceCatalogAppended);